/**
 * 租戶網路硬碟 API client（#23 增量 2）。fork myDriveService 的 tenant-drive 版：
 * owner scope = organization_id（隱含 from session）、base path /api/ee/tenant-drive。
 * 下載 helper（parseFilename / triggerBrowserDownload）reuse my_drive（泛型）。
 */
import axios from '../../../../api/axios';
import { parseFilenameFromDisposition, triggerBrowserDownload } from '../../myDrive/services/myDriveService';

const BASE = '/api/ee/tenant-drive';

export interface TenantDriveFile {
  id: number;
  relative_path: string;
  filename: string;
  size_bytes: number;
  mime_type: string;
  is_directory: boolean;
  file_object_id?: number | null;
  scan_status: string;
  created_at?: string;
}

export interface TenantMemberUsage {
  user_id: number;
  username: string;
  used_bytes: number;
}

export interface TenantDriveQuota {
  org_id: number;
  quota_bytes: number;          // 租戶總量（計費/上傳天花板）
  user_default_bytes: number;   // 每一使用者基本使用量
  org_drive_used: number;       // 租戶 drive（org-owned）用量
  members_used: number;         // Σ 成員個人 my_drive 用量
  total_used: number;           // 計費基準 = org + members
  members: TenantMemberUsage[];
}

export const listFiles = (prefix = '') =>
  axios.get<{ total: number; items: TenantDriveFile[] }>(`${BASE}/files`, { params: { prefix } })
    .then(r => r.data);

export const uploadFile = (file: File, relativePath = '') => {
  const fd = new FormData();
  fd.append('file', file);
  return axios.post(`${BASE}/files/upload`, fd, {
    params: { relative_path: relativePath },
    headers: { 'Content-Type': 'multipart/form-data' },   // axios 預設 json，必覆蓋
  }).then(r => r.data);
};

export const createFolder = (relativePath: string) =>
  axios.post<TenantDriveFile>(`${BASE}/folders`, { relative_path: relativePath }).then(r => r.data);

export const deleteFile = (fileId: number) =>
  axios.delete(`${BASE}/files/${fileId}`).then(r => r.data);

export const downloadFile = async (fileId: number, fallbackName?: string) => {
  const resp = await axios.get(`${BASE}/files/${fileId}/preview`, {
    params: { disposition: 'attachment' }, responseType: 'blob',
  });
  const dispo = (resp.headers['content-disposition'] as string) || '';
  const name = parseFilenameFromDisposition(dispo) || fallbackName || `file-${fileId}`;
  triggerBrowserDownload(resp.data as Blob, name);
};

export const previewUrl = (fileId: number) => `${BASE}/files/${fileId}/preview`;

// ── 配額 ──
export const getQuota = () =>
  axios.get<TenantDriveQuota>(`${BASE}/quota`).then(r => r.data);

// admin panel（SYSTEM_ADMIN）：讀 / 調任一租戶配額
export const getOrgQuota = (orgId: number) =>
  axios.get<TenantDriveQuota>(`${BASE}/quota/${orgId}`).then(r => r.data);

export const adjustQuota = (orgId: number, body: { quota_gb?: number; user_default_gb?: number }) =>
  axios.patch<TenantDriveQuota>(`${BASE}/quota/${orgId}`, body).then(r => r.data);
